import type { MaybeRefOrGetter } from 'vue';
import { computed, toValue } from 'vue';
import { slugify } from '~/utils/textSlug';
import { normalizeDeveloperName } from '~/utils/developerName';
import { useBreadcrumbSchema } from '~/composables/useSchemaOrg';
import { DEFAULT_LOCALE } from '~/constants/i18n';
import { localizePath, normalizeLocale } from '~/utils/i18n/locale';
import { seoText } from '~/utils/i18n/seo';

type LocaleText = { en?: string; ru?: string; ar?: string } | string | null | undefined;

type PropertySeoInput = {
  title?: LocaleText;
  description?: LocaleText;
  developer?: { title?: LocaleText } | null;
  address?: string | null;
  emirate?: string | null;
  album?: string[] | null;
  albums?: { images?: string[] }[] | null;
  photo?: string | null;
  price?: { min?: number | string | null; currency?: string | null } | null;
  complex_id?: string | number | null;
  id?: string | number | null;
  slug?: string | null;
  developerSlug?: string | null;
} & Record<string, unknown>;

// Production canonical. In dev/staging the runtime appUrl from NUXT_PUBLIC_SITE_URL
// can differ — see usePropertySeo() below which prefers the runtime value.
const SITE_BASE_DEFAULT = 'https://insidersofdubai.com';
const SITE_NAME = 'Insiders of Dubai';

const localeText = (value: LocaleText): string => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  return value.en || value.ru || value.ar || '';
};

const stripHtml = (value: string): string =>
  value
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const truncate = (value: string, max = 160): string => {
  if (value.length <= max) return value;
  return `${value.slice(0, max - 1).trimEnd()}…`;
};

const firstImage = (p: PropertySeoInput): string | null => {
  if (Array.isArray(p.album) && p.album.length) return p.album[0];
  if (Array.isArray(p.albums)) {
    for (const a of p.albums) {
      if (a?.images?.length) return a.images[0];
    }
  }
  return p.photo || null;
};

export const usePropertySeo = (property: MaybeRefOrGetter<PropertySeoInput | null | undefined>): void => {
  const runtimeConfig = useRuntimeConfig();
  const { t, locale } = useI18n();
  const { formatNumber } = useFormat();
  const siteBase = computed(() => {
    const fromEnv = (runtimeConfig.public.appUrl as string | undefined)?.trim();
    if (fromEnv) return fromEnv.replace(/\/$/, '');
    return SITE_BASE_DEFAULT;
  });
  const meta = computed(() => {
    const p = toValue(property);
    if (!p) return null;

    const title = localeText(p.title);
    const developer = localeText(p.developer?.title);
    const description = stripHtml(localeText(p.description));
    const slug = p.slug || slugify(title);
    const developerSlug = p.developerSlug || normalizeDeveloperName(developer);
    const id = String(p.complex_id ?? p.id ?? '').trim();
    const base = siteBase.value;
    const canonicalPath =
      slug && developerSlug ? `/projects/${developerSlug}/${slug}` : slug ? `/p/${slug}` : `/properties/${id}`;
    // Localized page: canonical/og:url point at the current locale's URL (English stays unprefixed).
    const canonical = `${base}${localizePath(canonicalPath, DEFAULT_LOCALE, normalizeLocale(locale.value) ?? DEFAULT_LOCALE)}`;
    const image = firstImage(p);
    const minPrice = Number(p.price?.min ?? 0) || null;
    const currency = (p.price?.currency || 'AED').toUpperCase();

    const headline = developer
      ? seoText(t('property.seo.titleWithDeveloper', { title, developer }), `${title} by ${developer}`)
      : title;
    const fallbackSummary = [
      headline,
      p.address,
      minPrice
        ? seoText(
            t('property.seo.fromPrice', { currency, price: formatNumber(minPrice) }),
            `${currency} ${formatNumber(minPrice)}`,
          )
        : '',
    ]
      .filter(Boolean)
      .join(' · ');
    const summary = truncate(description || fallbackSummary);

    const jsonLd: Record<string, unknown> = {
      '@context': 'https://schema.org',
      '@type': 'RealEstateListing',
      name: title,
      description: summary,
      url: canonical,
    };
    if (image) jsonLd.image = image;
    if (p.address || p.emirate) {
      jsonLd.address = {
        '@type': 'PostalAddress',
        addressLocality: p.address || undefined,
        addressRegion: p.emirate || 'Dubai',
        addressCountry: 'AE',
      };
    }
    if (minPrice) {
      jsonLd.offers = {
        '@type': 'Offer',
        priceCurrency: currency,
        price: minPrice,
      };
    }

    return {
      title: headline,
      summary,
      canonicalPath,
      canonical,
      image,
      jsonLd,
    };
  });

  const fallbackTitle = () => seoText(t('property.seo.fallbackTitle'), SITE_NAME);

  useCanonicalPathDeclaration(() => meta.value?.canonicalPath);

  useSeoMeta({
    title: () => meta.value?.title || fallbackTitle(),
    description: () => meta.value?.summary || '',
    ogTitle: () => meta.value?.title || fallbackTitle(),
    ogDescription: () => meta.value?.summary || '',
    ogImage: () => meta.value?.image || undefined,
    ogUrl: () => meta.value?.canonical || undefined,
    ogType: 'website',
    ogSiteName: SITE_NAME,
    twitterCard: 'summary_large_image',
    twitterTitle: () => meta.value?.title || fallbackTitle(),
    twitterDescription: () => meta.value?.summary || '',
    twitterImage: () => meta.value?.image || undefined,
  });

  useHead({
    link: computed(() => {
      const href = meta.value?.canonical;
      return href ? [{ rel: 'canonical', href }] : [];
    }),
    script: computed(() => {
      const ld = meta.value?.jsonLd;
      return ld ? [{ key: 'schema-org-project', type: 'application/ld+json', innerHTML: JSON.stringify(ld) }] : [];
    }),
  });

  // Breadcrumb trail: Home → Explore → <Developer> → <Project>. Reuses
  // the centralized breadcrumb schema helper so all entity pages emit
  // the same shape.
  useBreadcrumbSchema(() => {
    const p = toValue(property);
    if (!p) return [];
    const dev = localeText(p.developer?.title);
    const title = localeText(p.title);
    const developerSlug = p.developerSlug || (dev ? normalizeDeveloperName(dev) : '');
    return [
      { name: t('public.developers.home'), url: '/' },
      { name: t('nav.items.explore'), url: '/explore' },
      ...(dev && developerSlug ? [{ name: dev, url: `/developers/${developerSlug}` }] : []),
      { name: title || fallbackTitle(), url: meta.value?.canonical || '/' },
    ];
  });
};
